import * as Sentry from '@sentry/nextjs';
import { sanitizeSentryEvent } from './sanitize';

export { sanitizeSentryEvent } from './sanitize';
export { captureErrorOnce, markAsReported, wasReported } from './captureError';
export { attachSentryErrorReporting, reportApiError } from './apiError';

type ClientInitOptions = {
  project: string;
  apiUrl?: string;
};

type ServerInitOptions = {
  project: string;
};

const getDsn = () =>
  process.env.SENTRY_DSN || process.env.NEXT_PUBLIC_SENTRY_DSN;

const getEnvironment = () =>
  process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ?? process.env.NODE_ENV;

const SAMPLE_RATE = Number(process.env.NEXT_PUBLIC_SENTRY_SAMPLE_RATE ?? 0.1);
const TRACES_SAMPLE_RATE = Number(
  process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE ?? 0.1
);

const IGNORE_ERRORS = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  'Non-Error promise rejection captured',
  'ChunkLoadError',
  'Loading chunk',
  'Loading CSS chunk',
  /^Script error\.?$/,
];

const DENY_URLS = [
  /googletagmanager\.com/i,
  /google-analytics\.com/i,
  /connect\.facebook\.net/i,
  /heap(analytics)?\.com/i,
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
];

export function initClientSentry({ project, apiUrl }: ClientInitOptions): void {
  const dsn = getDsn();
  if (!dsn) return;

  const isReplayEnabled =
    process.env.NEXT_PUBLIC_SENTRY_REPLAY_ENABLED === 'true';

  Sentry.init({
    dsn,
    environment: getEnvironment(),
    sampleRate: SAMPLE_RATE,
    tracesSampleRate: TRACES_SAMPLE_RATE,
    tracePropagationTargets: apiUrl ? [apiUrl] : [],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      new Sentry.BrowserTracing(),
      ...(isReplayEnabled
        ? [
            new Sentry.Replay({
              maskAllText: true,
              maskAllInputs: true,
              blockAllMedia: false,
              networkDetailAllowUrls: apiUrl ? [apiUrl] : [],
            }),
          ]
        : []),
    ],
    initialScope: { tags: { project } },
    ignoreErrors: IGNORE_ERRORS,
    denyUrls: DENY_URLS,
    beforeSend: sanitizeSentryEvent,
  });
}

export function initServerSentry({ project }: ServerInitOptions): void {
  const dsn = getDsn();
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: getEnvironment(),
    sampleRate: SAMPLE_RATE,
    tracesSampleRate: TRACES_SAMPLE_RATE,
    initialScope: { tags: { project } },
    ignoreErrors: ['Non-Error promise rejection captured'],
    beforeSend: sanitizeSentryEvent,
  });
}

export function initEdgeSentry({ project }: ServerInitOptions): void {
  const dsn = getDsn();
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: getEnvironment(),
    sampleRate: SAMPLE_RATE,
    tracesSampleRate: TRACES_SAMPLE_RATE,
    initialScope: { tags: { project } },
    beforeSend: sanitizeSentryEvent,
  });
}
